/**
 * AccessContext — per-module permission lookup for the funnel workspace.
 *
 * NOTE ON THE DIRECTORY NAME: this file lives in `src/contexts/` (plural),
 * while AuthContext and DateRangeContext live in `src/context/` (singular).
 * Both paths are real and both are imported by name. Do not "tidy" them into
 * one directory without updating every importer — the split is load-bearing.
 *
 * WHAT THIS PROVIDES
 *   role          "viewer" | "editor" | "admin" | "owner"
 *   permissions   { [module]: "none" | "view" | "edit" }
 *   isOwner / isAdmin
 *   can(module, level = "view")   — synchronous, never throws
 *   reload()                      — re-derive after a role change
 *
 * DERIVED FROM THE SESSION, NOT A SEPARATE ENDPOINT.
 * The original implementation fetched a per-module permission map from a
 * team-management API that is not part of this package. Rather than ship a
 * call to an endpoint the backend does not serve — which would 404 on every
 * mount and silently degrade every gated control to "denied" — permissions
 * are derived here from the authenticated user's role, which `/api/auth/me`
 * already returns.
 *
 * If you run a richer permission model, this is the single seam to change:
 * replace `derive()` with a fetch, keep the returned shape identical, and
 * every call site keeps working unchanged.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";

const AccessContext = createContext(null);

const RANK = { viewer: 1, editor: 2, admin: 3, owner: 4 };

/** Roles at or above `editor` may edit; anyone signed in may view. */
function derive(user) {
  const role = (user && user.role) || "viewer";
  const rank = RANK[role] || 0;
  return {
    role,
    permissions: {},
    isOwner: role === "owner",
    isAdmin: rank >= RANK.admin,
    loading: false,
    can(module, level = "view") {
      if (!user) return false;
      // Platform settings stay owner-only, matching the backend's own rule.
      if (module === "settings") return role === "owner";
      if (level === "edit") return rank >= RANK.editor;
      return rank >= RANK.viewer;
    },
  };
}

/** Safe fallback used before the session resolves and outside the provider. */
const DEFAULT = {
  role: "viewer",
  permissions: {},
  isOwner: false,
  isAdmin: false,
  loading: true,
  reload: async () => {},
  can: () => false,
};

export function AccessProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(async () => setNonce((n) => n + 1), []);

  const value = useMemo(() => {
    if (authLoading) return { ...DEFAULT, reload };
    return { ...derive(user), reload };
    // `nonce` is a deliberate re-derive trigger for reload().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, reload, nonce]);

  useEffect(() => {
    /* no-op: kept so a future async permission fetch has a mount hook */
  }, [user]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess() {
  const ctx = useContext(AccessContext);
  return ctx || DEFAULT;
}
